import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { productService } from '../services/product.service';
import { ProductCard } from '../components/shop/ProductCard';
import { CategoryFilter } from '../components/shop/CategoryFilter';
import { Button } from '../components/ui/Button';
import { Loader2, Search, Filter, X } from 'lucide-react';

type SortBy = 'price_asc' | 'price_desc' | 'newest' | 'popular' | 'rating';

export const ShopPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [sortBy, setSortBy] = useState<SortBy>((searchParams.get('sort') as SortBy) || 'newest');
  const [priceRange, setPriceRange] = useState<{ min?: number; max?: number }>({
    min: searchParams.get('minPrice') ? Number(searchParams.get('minPrice')) : undefined,
    max: searchParams.get('maxPrice') ? Number(searchParams.get('maxPrice')) : undefined,
  });
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['products', { category: selectedCategory, search: searchTerm, sort: sortBy, ...priceRange, page }],
    queryFn: () =>
      productService.getProducts({
        category: selectedCategory || undefined,
        search: searchTerm || undefined,
        minPrice: priceRange.min,
        maxPrice: priceRange.max,
        sort: sortBy,
        page,
        limit: 12,
      }),
  });

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedCategory) params.set('category', selectedCategory);
    if (searchTerm) params.set('search', searchTerm);
    if (sortBy !== 'newest') params.set('sort', sortBy);
    if (priceRange.min) params.set('minPrice', String(priceRange.min));
    if (priceRange.max) params.set('maxPrice', String(priceRange.max));
    if (page > 1) params.set('page', String(page));
    setSearchParams(params);
  }, [selectedCategory, searchTerm, sortBy, priceRange, page]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSearchTerm('');
    setSortBy('newest');
    setPriceRange({});
    setPage(1);
  };

  const hasFilters = selectedCategory || searchTerm || sortBy !== 'newest' || priceRange.min || priceRange.max;

  return (
    <div className="container mx-auto px-3 py-5 sm:px-4 sm:py-8">
      <div className="mb-5 sm:mb-8">
        <h1 className="text-2xl font-bold text-[rgb(var(--foreground))] sm:text-3xl">Shop</h1>
        <p className="text-[rgb(var(--light-secondary))]">Premium devices, accessories, and parts</p>
      </div>

      {/* Search and Filters Bar */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <form onSubmit={handleSearch} className="flex flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--light-secondary))]" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-l-lg border border-[rgb(var(--border))] bg-[rgb(var(--background))] pl-10 pr-4 py-2 text-[rgb(var(--foreground))] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <Button type="submit" variant="primary" size="md" className="rounded-l-none">
            Search
          </Button>
        </form>

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden"
          >
            <Filter className="mr-2 h-4 w-4" />
            Filters
          </Button>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
            className="rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--background))] px-4 py-2 text-[rgb(var(--foreground))] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary appearance-none"
          >
            <option value="newest">Newest</option>
            <option value="popular">Most Popular</option>
            <option value="rating">Top Rated</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
          </select>

          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-500">
              <X className="mr-1 h-4 w-4" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Filters Panel */}
      <div className={`mt-4 ${showFilters ? 'block' : 'hidden md:block'}`}>
        <div className="flex flex-wrap gap-4 rounded-lg border border-[rgb(var(--border))] p-4 bg-[rgb(var(--card))]">
          <div className="flex-1 min-w-50">
            <label className="mb-1 block text-sm font-medium">Category</label>
            <CategoryFilter
              selectedCategory={selectedCategory}
              onSelect={setSelectedCategory}
            />
          </div>
          <div className="flex-1 min-w-50">
            <label className="mb-1 block text-sm font-medium">Price Range (GHS)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder="Min"
                value={priceRange.min || ''}
                onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--background))] px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
              <span className="text-[rgb(var(--light-secondary))]">-</span>
              <input
                type="number"
                placeholder="Max"
                value={priceRange.max || ''}
                onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--background))] px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="mt-8">
        {isLoading || isFetching ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : data?.data?.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-[rgb(var(--light-secondary))]">No products found. Try adjusting your filters.</p>
            {hasFilters && (
              <Button variant="secondary" onClick={clearFilters} className="mt-4">
                Clear all filters
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="mb-4 text-sm text-[rgb(var(--light-secondary))]">
              Showing {data?.data?.length || 0} of {data?.pagination?.total || 0} products
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 xl:grid-cols-4">
              {data?.data?.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {/* Pagination */}
            {data?.pagination && data.pagination.totalPages > 1 && (
              <div className="mt-8 flex justify-center gap-2">
                {Array.from({ length: Math.min(5, data.pagination.totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-4 py-2 rounded-lg border ${
                        pageNum === page
                          ? 'border-primary bg-primary text-white'
                          : 'border-[rgb(var(--border))] text-[rgb(var(--foreground))] hover:bg-[rgb(var(--border))]'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {data.pagination.totalPages > 5 && (
                  <>
                    <span className="px-2 py-2 text-[rgb(var(--light-secondary))]">...</span>
                    <button
                      onClick={() => setPage(data.pagination.totalPages)}
                      className={`px-4 py-2 rounded-lg border ${
                        data.pagination.totalPages === page
                          ? 'border-primary bg-primary text-white'
                          : 'border-[rgb(var(--border))] text-[rgb(var(--foreground))] hover:bg-[rgb(var(--border))]'
                      }`}
                    >
                      {data.pagination.totalPages}
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};